import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { DeviceChangeOption } from '../../../../common/enums';
import { KeyUserDeviceChange } from '../../../DeviceCheckoutCartProduct/KeyUserDeviceChange';
import { getExpiringEppDeviceSubscriptions } from '../../../SubscriptionDetails/subscriptionDetailsCommon';
import type { DeviceChangeOrRedeemRequest } from '../../../../common/types/device';
import type { CompanyInfoResponse } from '../../../../generated/api/companyInfoResponse';
import type { EppRedeemTerminateRequestType } from '../../../../generated/api/eppRedeemTerminateRequestType';
import type { Subscription } from '../../../../generated/api/subscription';
import type { PlaceOrderFormData, PlaceOrderItemUnit } from '../../placeOrderTypes';

export interface KeyUserDeviceChangeHandlerProps {
  orderItem: PlaceOrderItemUnit;
  refId: string;
  companyInfo: CompanyInfoResponse;
  expiringEppSubscriptions: Subscription[];
  isFirstOrderItem: boolean;
  commercialProductCode: string;
  selectedContactId: string;
}

export const KeyUserDeviceChangeHandler = ({
  orderItem,
  refId,
  companyInfo,
  expiringEppSubscriptions,
  isFirstOrderItem,
  commercialProductCode,
  selectedContactId,
}: KeyUserDeviceChangeHandlerProps) => {
  const methods = useFormContext<PlaceOrderFormData>();

  const expiringEppDeviceSubscriptions = getExpiringEppDeviceSubscriptions(expiringEppSubscriptions, []);

  // Watch form values for this order item
  const allOrderItemUnits = methods.watch('products.placeOrderItemUnits');
  const orderItemIndex = allOrderItemUnits.findIndex(
    item => item.basketItemId === orderItem.basketItemId && item.basketItemIndex === orderItem.basketItemIndex
  );
  const deviceChange = methods.watch(`products.placeOrderItemUnits.${orderItemIndex}.deviceChange`);

  // Check if device change is already selected for another order item
  const isDeviceChangeAlreadySelected = allOrderItemUnits.some(
    (item, idx) => item.deviceChange?.replacedSubscriptionId && idx !== orderItemIndex
  );

  // Clear device change when contact changes and no longer matches
  useEffect(() => {
    if (
      deviceChange?.replacedSubscriptionContactId &&
      deviceChange.replacedSubscriptionContactId !== selectedContactId
    ) {
      methods.setValue(`products.placeOrderItemUnits.${orderItemIndex}.deviceChange`, undefined);
    }
  }, [selectedContactId, deviceChange?.replacedSubscriptionContactId, methods, orderItemIndex]);

  // Handle device change selection from KeyUserDeviceChange component
  const handleDeviceChangeSelection = (
    contactId: string,
    subscriptionId?: string,
    subscriptionBillingAccountId?: string,
    redeemType?: EppRedeemTerminateRequestType
  ) => {
    if (subscriptionId) {
      const deviceChangeRequest: DeviceChangeOrRedeemRequest = {
        deviceChangeOption: redeemType ? DeviceChangeOption.REDEEM : DeviceChangeOption.RETURN,
        replacementDeviceCommercialProductCode: commercialProductCode,
        replacedSubscriptionId: subscriptionId,
        replacedSubscriptionContactId: contactId,
        replacedSubscriptionBillingAccountId: subscriptionBillingAccountId,
        redeemRequestType: redeemType,
      };
      methods.setValue(`products.placeOrderItemUnits.${orderItemIndex}.deviceChange`, deviceChangeRequest);
    } else {
      // User selected "New Contract" - clear device change
      methods.setValue(`products.placeOrderItemUnits.${orderItemIndex}.deviceChange`, undefined);
    }
  };

  return (
    <KeyUserDeviceChange
      onSelectSubscriptionForDeviceChange={handleDeviceChangeSelection}
      selectedPouIndex={refId}
      isDeviceChangeAlreadySelected={isDeviceChangeAlreadySelected}
      deviceChangeRequest={deviceChange}
      expiringEppDeviceSubscription={expiringEppDeviceSubscriptions}
      contactId={selectedContactId}
      subscriptionIdForDeviceChange={deviceChange?.replacedSubscriptionId}
      isDeviceChangeSelectedWithSubscriptionId={!!deviceChange?.replacedSubscriptionId}
      isFirstCartItem={isFirstOrderItem}
      companyName={companyInfo.companyName}
    />
  );
};
